import { useState } from 'react';
import {
  Group,
  Header,
  SimpleCell,
  Button,
  Input,
  FormItem,
  Text,
  Div,
} from '@vkontakte/vkui';
import { useAdminMaps } from '../../api/hooks';
import { api } from '../../api/client';

type AdminMap = {
  id: number;
  title: string;
  description: string | null;
  downloadUrl: string;
  status: string;
  scenarioHash: string | null;
  sortOrder: number;
  createdAt: string;
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Черновик',
  active: 'Опубликована',
  scheduled: 'Запланирована',
  archived: 'Архив',
};

function truncateHash(hash: string | null) {
  if (!hash) return '—';
  if (hash.length <= 16) return hash;
  return `${hash.slice(0, 8)}…${hash.slice(-8)}`;
}

function MapEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: { title: string; downloadUrl: string; scenarioHash: string };
  onSave: (data: { title: string; downloadUrl: string; scenarioHash: string }) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial.title);
  const [downloadUrl, setDownloadUrl] = useState(initial.downloadUrl);
  const [scenarioHash, setScenarioHash] = useState(initial.scenarioHash);

  return (
    <Div style={{ padding: '8px 0' }}>
      <FormItem top="Название">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </FormItem>
      <FormItem top="Ссылка">
        <Input value={downloadUrl} onChange={(e) => setDownloadUrl(e.target.value)} />
      </FormItem>
      <FormItem top="Scenario hash (только для админки)">
        <Input
          value={scenarioHash}
          onChange={(e) => setScenarioHash(e.target.value)}
          placeholder="64-символьный хеш QuadroSim"
        />
      </FormItem>
      <Div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Button
          stretched
          className="pb-gradient-btn"
          disabled={!title.trim() || !downloadUrl.trim()}
          onClick={() =>
            onSave({
              title: title.trim(),
              downloadUrl: downloadUrl.trim(),
              scenarioHash: scenarioHash.trim(),
            })
          }
        >
          Сохранить
        </Button>
        <Button stretched mode="secondary" onClick={onCancel}>
          Отмена
        </Button>
      </Div>
    </Div>
  );
}

export function AdminMaps() {
  const { data, refetch } = useAdminMaps();
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [scenarioHash, setScenarioHash] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const maps = (data?.maps ?? []) as AdminMap[];

  const create = async () => {
    await api('/api/admin/maps', {
      method: 'POST',
      body: JSON.stringify({
        title: title.trim(),
        downloadUrl: url.trim(),
        scenarioHash: scenarioHash.trim() || undefined,
      }),
    });
    setTitle('');
    setUrl('');
    setScenarioHash('');
    refetch();
  };

  const saveEdit = async (
    id: number,
    payload: { title: string; downloadUrl: string; scenarioHash: string },
  ) => {
    setBusyId(id);
    try {
      await api(`/api/admin/maps/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: payload.title,
          downloadUrl: payload.downloadUrl,
          scenarioHash: payload.scenarioHash || null,
        }),
      });
      setEditingId(null);
      refetch();
    } finally {
      setBusyId(null);
    }
  };

  const togglePublish = async (map: AdminMap) => {
    setBusyId(map.id);
    try {
      if (map.status === 'active') {
        await api(`/api/admin/maps/${map.id}/unpublish`, { method: 'POST' });
      } else {
        await api(`/api/admin/maps/${map.id}/publish`, { method: 'POST' });
      }
      refetch();
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (map: AdminMap) => {
    const label = STATUS_LABEL[map.status] ?? map.status;
    if (
      !window.confirm(
        `Удалить карту «${map.title}» (${label})? Это действие необратимо.`,
      )
    ) {
      return;
    }
    setBusyId(map.id);
    try {
      await api(`/api/admin/maps/${map.id}`, { method: 'DELETE' });
      if (editingId === map.id) setEditingId(null);
      refetch();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Group header={<Header size="s">Новая карта</Header>} className="pb-glow-card">
        <FormItem top="Название">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </FormItem>
        <FormItem top="Ссылка на скачивание">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} />
        </FormItem>
        <FormItem
          top="Scenario hash"
          bottom="Не показывается участникам. Нужен для лидерборда QuadroSim."
        >
          <Input
            value={scenarioHash}
            onChange={(e) => setScenarioHash(e.target.value)}
            placeholder="64 hex-символа"
          />
        </FormItem>
        <FormItem>
          <Button
            stretched
            className="pb-gradient-btn"
            disabled={!title.trim() || !url.trim()}
            onClick={create}
          >
            Создать
          </Button>
        </FormItem>
      </Group>

      <Group header={<Header size="s">Карты ({maps.length})</Header>}>
        {maps.length === 0 && (
          <SimpleCell>
            <Text className="pb-text-secondary">Карт пока нет</Text>
          </SimpleCell>
        )}
        {maps.map((m) => (
          <div key={m.id} className="pb-map-card pb-glow-card">
            {editingId === m.id ? (
              <MapEditor
                initial={{
                  title: m.title,
                  downloadUrl: m.downloadUrl,
                  scenarioHash: m.scenarioHash ?? '',
                }}
                onSave={(payload) => saveEdit(m.id, payload)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <>
                <SimpleCell
                  subtitle={
                    <>
                      <Text className="pb-text-secondary">
                        {STATUS_LABEL[m.status] ?? m.status}
                      </Text>
                      <Text className="pb-text-secondary" style={{ marginTop: 4 }}>
                        Hash: {truncateHash(m.scenarioHash)}
                      </Text>
                    </>
                  }
                  multiline
                >
                  {m.title}
                </SimpleCell>
                <Div className="pb-map-card__actions">
                  <Button
                    size="s"
                    mode="secondary"
                    stretched
                    disabled={busyId === m.id}
                    onClick={() => setEditingId(m.id)}
                  >
                    Изменить
                  </Button>
                  <Button
                    size="s"
                    stretched
                    className={m.status === 'active' ? undefined : 'pb-gradient-btn'}
                    mode={m.status === 'active' ? 'secondary' : undefined}
                    disabled={busyId === m.id}
                    onClick={() => togglePublish(m)}
                  >
                    {m.status === 'active' ? 'Снять' : 'Опубликовать'}
                  </Button>
                  <Button
                    size="s"
                    mode="tertiary"
                    stretched
                    disabled={busyId === m.id}
                    onClick={() => remove(m)}
                  >
                    Удалить
                  </Button>
                </Div>
              </>
            )}
          </div>
        ))}
      </Group>
    </>
  );
}
